import React, { FC, useContext } from "react";

import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { TrainingType } from "@/features/training/pages/Archive/panel-left/ArchivePanelLeft";
import {
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Stack,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(utc);

const inputSx = {
	minWidth: 145,
	"& .MuiOutlinedInput-root": { borderRadius: 1.5 },
};

interface TrainingArchiveSearchProps {
	startDate?: Date;
	endDate?: Date;
	trainingType: TrainingType;
	setStartDate: (date: Date) => void;
	setEndDate: (date: Date) => void;
	setTrainingType: (type: TrainingType) => void;
}

export const TrainingArchiveSearch: FC<TrainingArchiveSearchProps> = (
	props,
) => {
	const translation: Language | null = useContext(TranslationContext);
	const { timezone } = useTime();

	const startDayjs = props.startDate
		? dayjs.utc(props.startDate).tz(timezone)
		: null;
	const endDayjs = props.endDate ? dayjs.utc(props.endDate).tz(timezone) : null;

	const handleStartChange = (date: Dayjs | null) => {
		if (date) props.setStartDate(date.toDate());
	};

	const handleEndChange = (date: Dayjs | null) => {
		if (date) props.setEndDate(date.toDate());
	};

	const onTrainingTypeChange = (e: SelectChangeEvent<TrainingType>) => {
		props.setTrainingType(e.target.value as TrainingType);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Stack
				direction="row"
				flexWrap="nowrap"
				useFlexGap
				gap={2}
				alignItems="center"
			>
				<DesktopDatePicker
					label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Archive.PanelLeft.TrainingArchiveSearch.L68C12.Start", "Start")}
					value={startDayjs}
					onChange={handleStartChange}
					slotProps={{
						textField: {
							size: "small",
							sx: inputSx,
						},
					}}
				/>
				<DesktopDatePicker
					label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Archive.PanelLeft.TrainingArchiveSearch.L79C12.End", "End")}
					value={endDayjs}
					onChange={handleEndChange}
					slotProps={{
						textField: {
							size: "small",
							sx: inputSx,
						},
					}}
				/>
				<FormControl
					size="small"
					sx={{
						minWidth: 130,
						"& .MuiOutlinedInput-root": { borderRadius: 1.5 },
					}}
				>
					<InputLabel id="training-type-label">
						<Translate String="Training.Archive.SessionType">Type</Translate>
					</InputLabel>
					<Select
						labelId="training-type-label"
						value={props.trainingType}
						label={
							<Translate String="Training.Archive.SessionType">Type</Translate>
						}
						onChange={onTrainingTypeChange}
					>
						<MenuItem value={TrainingType.All}>
							<Translate String="Training.Archive.SessionType.All">
								{TrainingType.All}
							</Translate>
						</MenuItem>
						<MenuItem value={TrainingType.Courses}>
							<Translate String="Training.Archive.SessionType.Courses">
								{TrainingType.Courses}
							</Translate>
						</MenuItem>
						<MenuItem value={TrainingType.Exercises}>
							<Translate String="Training.Archive.SessionType.Exercises">
								{TrainingType.Exercises}
							</Translate>
						</MenuItem>
						<MenuItem value={TrainingType.Events}>
							<Translate String="Training.Archive.SessionType.Events">
								{TrainingType.Events}
							</Translate>
						</MenuItem>
					</Select>
				</FormControl>
			</Stack>
		</LocalizationProvider>
	);
};
